import { Skeleton, Typography } from '@mui/material';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import React, { useEffect, useState } from 'react';
import StyledTable from '../../commun/table';
import NoData from '../../commun/no-data';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';
import { searchLocalAuthorities } from '../../../services/composition.service';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import { SearchLocalAuthorityDto } from '../../../services/dtos/search-local-authority.dto';
import { LocalAuthorityDto } from '../../../services/dtos/local-authority.dto';
import LocalAuthorityForm from '../../forms/composition/local-authority.form';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';

/**
 * Ui de l'onglet composition collectivités rattachées
 * @constructor
 */
export default function SubCompositionUi(
    props: Readonly<{
        mandate: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
    }>
) {
    const establishmentId = 'establishmentId' in props.mandate ? props.mandate.establishmentId : undefined;
    const territoryId = 'territoryId' in props.mandate ? props.mandate.territoryId : undefined;

    //SnackBar pour les messages
    const showMessage = useSnackbars();
    //Searching
    const [searching, setSearching] = useState<boolean>(false);

    //Critères de recherche onglet composition
    const [searchLocalAuthority, setSearchLocalAuthority] = useState<SearchLocalAuthorityDto>({
        page: 0,
        itemsPerPage: 10,
        mandateId: props.mandate.id,
        etablissementId: establishmentId,
        territoireId: territoryId,
    });
    // result onglet composition
    const [resultLocalAuthority, setResultaLocalAuthority] = useState<ResultSearchDto<LocalAuthorityDto>>();

    //Lancement de la recherche a chaque changement
    useEffect(() => {
        let isMounted = true;

        setSearching(true);
        const data: SearchLocalAuthorityDto = {
            ...searchLocalAuthority,
        };

        searchLocalAuthorities(data)
            .then(result => {
                if (isMounted) {
                    setResultaLocalAuthority(result);
                }
            })
            .catch(error => {
                if (!isMounted) {
                    return;
                }

                console.error('Erreur lors de la récupération des compositions');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                if (isMounted) {
                    setSearching(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [searchLocalAuthority, showMessage]);

    const handlerSearch = (paramForm: LocalAuthorityDto) => {
        if (searchLocalAuthority) {
            const newSearchData: SearchLocalAuthorityDto = {
                ...searchLocalAuthority,
                naming: paramForm.naming,
                membershipDate: paramForm.membershipDate,
                establishment: paramForm.establishment,
                authority: paramForm.authority,
                etablissementId: establishmentId,
            };
            setSearchLocalAuthority(newSearchData);
        }
    };

    //Méthode au changement de la page
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setSearchLocalAuthority({
            ...searchLocalAuthority,
            page: newPage,
        });
    };

    //Méthode au changement d'items par page
    const handleChangeItemsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchLocalAuthority({
            ...searchLocalAuthority,
            page: 0,
            itemsPerPage: Number.parseInt(event.target.value, 10),
        });
    };

    //handler sort
    const handlerSortBy = (order: 'asc' | 'desc', sort: keyof LocalAuthorityDto) => {
        setSearchLocalAuthority({
            ...searchLocalAuthority,
            order: order,
            sort: sort,
        });
    };

    // Création des cellules têtes du tableau
    // Les ids des cellules doivent correspondre aux noms des champs DTO, sauf l'action
    const headCells: HeadCellDto<LocalAuthorityDto, keyof LocalAuthorityDto>[] = [
        { id: 'naming', label: 'Désignation', sort: true },
        { id: 'membershipDate', label: 'Date d’adhésion', sort: true },
    ];

    //Premet de conditionner l'affichage du tableau
    const showTable = () => {
        if (resultLocalAuthority === undefined) {
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        if (resultLocalAuthority?.totalItems === 0) {
            return <NoData title={'Information : '} content={'Aucune donnée trouvée'} />;
        } else {
            return (
                <StyledTable
                    data={resultLocalAuthority?.items}
                    searching={searching}
                    headCells={headCells}
                    totalItems={resultLocalAuthority?.totalItems}
                    page={searchLocalAuthority.page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeItemsPerPage}
                    itemsPerPage={searchLocalAuthority.itemsPerPage}
                    handlerSortBy={handlerSortBy}
                    order={searchLocalAuthority.order}
                    sort={searchLocalAuthority.sort}
                ></StyledTable>
            );
        }
    };

    //Gestion du titre
    let title = '';
    if ('title' in props.mandate) {
        title = props.mandate.title;
    }
    if ('establishmentLabel' in props.mandate) {
        title = props.mandate.establishmentLabel;
    }
    if ('territoryLabel' in props.mandate) {
        title = props.mandate.territoryLabel;
    }

    //Retourne la vue
    return (
        <Typography component="div" className={'tab-content'}>
            <Typography variant="h3" className={'title margin-top-50'}>
                <LocationCityIcon /> Collectivités rattachées : {title}
            </Typography>
            {/*On affiche le formulaire*/}
            <LocalAuthorityForm handlerSearch={handlerSearch} searching={searching} />
            {/*On affiche le tableau*/}
            {showTable()}
        </Typography>
    );
}
